import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from '@angular/core';

import { RagResult } from '../../models/rag-result.model';
import { Snippet } from '../../models/snippet.model';

/** term: a word from the question. answer: meta.most_frequent_answer. */
interface TextPart { text: string; mark: 'term' | 'answer' | null; }

interface PassageView {
  index: number;
  snippet: Snippet;
  parts: TextPart[];
}

interface SourceView {
  url: string;
  title: string;
  host: string;
  favicon: string | null;
  publishedAt: string | null;
  passages: number;
  bestScore: number | null;
}

/** The include_* toggles whose fields a passage can show, for the
 *  "turn these on" tip when none of the passages carry them. Domain name
 *  isn't listed: the card always shows a domain, read off the URL if needed. */
const OPTIONAL_FIELDS: { label: string; has: (s: Snippet) => boolean }[] = [
  { label: 'Relevance score', has: s => s.relevanceScore !== null },
  { label: 'Article title', has: s => !!s.articleTitle },
  { label: 'Favicon', has: s => !!s.favicon },
  { label: 'Image URL', has: s => !!s.imageUrl },
  { label: 'Original-language snippet', has: s => !!s.originalLanguageSnippet },
];

const STOPWORDS = new Set([
  'what', 'which', 'with', 'from', 'have', 'that', 'this', 'they', 'their', 'about', 'last', 'were', 'been',
  'does', 'into', 'over', 'than', 'then', 'when', 'where', 'will', 'your', 'there', 'these', 'those', 'months',
  'month', 'years', 'year', 'weeks', 'week', 'days', 'many', 'much', 'some', 'more', 'most', 'also',
]);

/** What each response key means, for the hover tips on the result view
 *  (wording follows the API docs' `snippet` / `rag_meta` schemas). */
const FIELD_TIPS = {
  relevanceScore: 'How closely this passage matches your question, from 0 to 1. Higher is better.',
  publishedAt: 'When the source published the article.',
  domainName: 'The site the article was published on. Click to open the original article.',
  url: 'The article this passage was taken from. Shown as its domain; click to open it.',
  articleTitle: 'Title of the article the passage comes from.',
  snippet: 'The passage itself, machine-translated to English. Words from your question are highlighted in yellow, the most frequent answer in blue.',
  originalLanguageSnippet: 'The same passage in the language the article was written in.',
  imageUrl: "The lead image of the article the passage comes from.",
  mostFrequentAnswer: 'A short answer span extracted from the matching passages, not generated. Best for simple factual questions; often empty.',
  count: 'How many passages came back, best match first.',
} as const;

/** Questions the extractive answer model suits: short, factual, unambiguous.
 *  Put into the question on click, not run (a run costs credits). */
const ANSWER_EXAMPLES = [
  'Who is the CEO of Nestlé?',
  'How many countries are in the European Union?',
  "What is Ulf Kristersson's role?",
  'In which country is Liège located?',
] as const;

const PAGE_SIZE = 20;

/**
 * /rag's Response tab: the passages as a reading list (best first) or
 * grouped by the article they came from. A passage is text quoted from an
 * article, not the article: the text leads, and the article (domain, date,
 * title, image) is the attribution under it. Article cards are for the
 * endpoints that return articles (/search, /similar).
 *
 * Top sites (../../top-sites) filters both views by domain, several at once. On a
 * wide panel it sits in a side column with the most frequent answer and
 * the include_* tip, so the list keeps a reading width without leaving
 * the right side empty; on a narrow one it's a row of chips above. Reads only what the response actually carries;
 * fields behind an off include_* toggle are left out, with one tip naming
 * the toggles that would fill them in.
 */
@Component({
  selector: 'app-rag-results',
  standalone: false,
  templateUrl: './rag-results.html',
  styleUrl: './rag-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RagResults {
  readonly result = input.required<RagResult>();
  /** The question as typed: its words are highlighted in the passages. */
  readonly question = input<string>('');
  /** An example question picked in the answer box, for the form to take. */
  readonly exampleQuestion = output<string>();

  protected readonly view = signal<'passages' | 'sources'>('passages');
  protected readonly visible = signal(PAGE_SIZE);
  /** Passages showing their original language instead of the English text. */
  protected readonly showingOriginal = signal<ReadonlySet<number>>(new Set());
  /** Passages expanded past the five-line clamp. */
  protected readonly expanded = signal<ReadonlySet<number>>(new Set());
  /** Passages whose clamped text is actually cut (measured), so they get a Show more. */
  protected readonly overflowing = signal<ReadonlySet<number>>(new Set());
  /** Show only passages/sources from these domains (Top sites); empty = all. */
  protected readonly siteFilter = signal<ReadonlySet<string>>(new Set());
  /** Passages whose thumbnail failed to load: their source line drops it. */
  protected readonly brokenImages = signal<ReadonlySet<number>>(new Set());
  protected readonly answerOpen = signal(true);
  protected readonly tips = FIELD_TIPS;

  private readonly terms = computed(() => termsOf(this.result().question || this.question()));
  protected readonly answer = computed(() => this.result().meta.mostFrequentAnswer?.trim() || '');
  /** "How is this found?" and its examples: open straight away when there's
   *  no answer (that's when they help), closed otherwise; reset per result. */
  protected readonly aboutOpen = linkedSignal(() => !this.answer());
  protected readonly examplesOpen = linkedSignal(() => !this.answer());
  protected readonly answerExamples = ANSWER_EXAMPLES;

  protected readonly passages = computed<PassageView[]>(() => {
    const terms = this.terms();
    const answer = this.answer();
    return this.result().snippets.map((snippet, index) => ({
      index,
      snippet,
      parts: highlight(snippet.snippet, terms, answer),
    }));
  });

  /** Whether the most frequent answer appears in any passage, so the
   *  answer box can say it's highlighted there. */
  protected readonly answerInPassages = computed(() => this.passages().some(p => p.parts.some(part => part.mark === 'answer')));

  protected readonly filteredPassages = computed(() => {
    const sites = this.siteFilter();
    return sites.size ? this.passages().filter(p => sites.has(p.snippet.host)) : this.passages();
  });

  protected readonly shownPassages = computed(() => this.filteredPassages().slice(0, this.visible()));


  protected readonly sources = computed<SourceView[]>(() => {
    const byUrl = new Map<string, SourceView>();
    const sites = this.siteFilter();
    for (const s of this.result().snippets) {
      if (sites.size && !sites.has(s.host)) continue;
      const existing = byUrl.get(s.url);
      if (existing) {
        existing.passages++;
        if (s.relevanceScore !== null && (existing.bestScore === null || s.relevanceScore > existing.bestScore)) {
          existing.bestScore = s.relevanceScore;
        }
      } else {
        byUrl.set(s.url, {
          url: s.url,
          title: s.articleTitle || s.host,
          host: s.host,
          favicon: s.favicon,
          publishedAt: s.publishedAt,
          passages: 1,
          bestScore: s.relevanceScore,
        });
      }
    }
    return [...byUrl.values()].sort((a, b) => b.passages - a.passages || (b.bestScore ?? 0) - (a.bestScore ?? 0));
  });

  /** Optional fields that no passage carries, i.e. toggles that are off. */
  protected readonly missingFields = computed(() => {
    const snippets = this.result().snippets;
    if (!snippets.length) return [];
    return OPTIONAL_FIELDS.filter(f => !snippets.some(f.has)).map(f => f.label);
  });

  protected setView(view: 'passages' | 'sources'): void {
    this.view.set(view);
  }

  /** Click a site to add it to the filter, again to take it out. */
  protected toggleSite(host: string): void {
    this.siteFilter.update(set => toggled(set, host));
    this.visible.set(PAGE_SIZE);
  }

  protected clearSites(): void {
    this.siteFilter.set(new Set());
    this.visible.set(PAGE_SIZE);
  }

  protected setOverflowing(index: number, value: boolean): void {
    if (this.overflowing().has(index) === value) return;
    this.overflowing.update(set => toggled(set, index));
  }

  protected showMore(): void {
    this.visible.update(n => n + PAGE_SIZE);
  }

  protected toggleOriginal(index: number): void {
    this.showingOriginal.update(set => toggled(set, index));
  }

  protected toggleExpanded(index: number): void {
    this.expanded.update(set => toggled(set, index));
  }

  protected hasImage(p: PassageView): boolean {
    return !!p.snippet.imageUrl && !this.brokenImages().has(p.index);
  }

  protected imageFailed(index: number): void {
    this.brokenImages.update(set => new Set(set).add(index));
  }

  /** A favicon that 404s shouldn't leave a broken-image icon. */
  protected hideBrokenImage(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}

function toggled<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  return next;
}

function termsOf(question: string): string[] {
  const words = (question.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [])
    .filter(w => w.length >= 4 && !STOPWORDS.has(w));
  return [...new Set(words)];
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Splits text into plain and marked parts, so only matches get <mark>
 *  (no innerHTML: the passages are third-party text). The most frequent
 *  answer is marked first; question words are marked in the rest. */
function highlight(text: string, terms: string[], answer: string): TextPart[] {
  if (!text) return [{ text, mark: null }];
  if (!answer) return highlightTerms(text, terms);
  // With a capturing group, split() puts the matches at the odd indexes.
  return text.split(new RegExp(`(${escapeRegExp(answer)})`, 'giu')).flatMap((chunk, i) => {
    if (!chunk) return [];
    return i % 2 ? [{ text: chunk, mark: 'answer' as const }] : highlightTerms(chunk, terms);
  });
}

function highlightTerms(text: string, terms: string[]): TextPart[] {
  if (!terms.length) return [{ text, mark: null }];
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'giu');
  const parts: TextPart[] = text.split(re).filter(Boolean)
    .map(part => ({ text: part, mark: terms.includes(part.toLowerCase()) ? 'term' as const : null }));

  // "Donald" + " " + "Trump" becomes one highlight, not two blocks with a gap.
  const merged: TextPart[] = [];
  for (let i = 0; i < parts.length; i++) {
    const prev = merged[merged.length - 1];
    const part = parts[i];
    if (part.mark && prev?.mark) {
      prev.text += part.text;
    } else if (!part.mark && prev?.mark && /^\s+$/.test(part.text) && parts[i + 1]?.mark) {
      prev.text += part.text;
    } else {
      merged.push({ ...part });
    }
  }
  return merged;
}

